import type { BoardView } from './BoardView';
import { TileStackHighlight, TileStackHighlightDisplayMode } from './TileStackHighlight';
import type { TileStackModel } from '../../models/board/TileStackModel';

export class BoardHighlightLayer {
  private static readonly tilesForTallHighlight = 5;

  private readonly lockedHighlightReasons = new Set<string>();

  private currentHighlight: string | null = null;

  private readonly highlights = new Map<string, TileStackHighlight>();

  constructor(private readonly boardView: BoardView) {}

  public createHighlight(stackModel: TileStackModel): TileStackHighlight {
    const { boardView } = this;

    const { x, y, zIndex } = boardView.getStackViewProps(stackModel);

    const view = new TileStackHighlight().props({
      x,
      y,
      zIndex: zIndex + 10,
      parent: boardView.getStackContainer(),
    });
    this.highlights.set(stackModel.id, view);

    const stackView = boardView.getStackView(stackModel.id);
    stackView?.once('destroyed', () => {
      if (this.highlights.has(stackModel.id)) {
        this.highlights.get(stackModel.id)?.destroy();
        this.highlights.delete(stackModel.id);
      }
    });

    return view;
  }

  public getHighlight(stackId: string): TileStackHighlight | null {
    return this.highlights.get(stackId) ?? null;
  }

  public lockSelection(reason: string): void {
    this.lockedHighlightReasons.add(reason);
  }

  public unlockSelection(reason: string): void {
    this.lockedHighlightReasons.delete(reason);
  }

  public setSelectionHighlight(tileStackId: string | null, immediate = false): void {
    if (this.lockedHighlightReasons.size > 0) return;

    if (this.currentHighlight === tileStackId) return;

    const duration = immediate ? 0 : undefined;

    if (this.currentHighlight) {
      this.getHighlight(this.currentHighlight)?.hide(duration);
    }

    this.currentHighlight = tileStackId;
    if (this.currentHighlight === null) return;
    this.getHighlight(this.currentHighlight)
      ?.setDisplayMode(this.getModeForStack(this.currentHighlight))
      ?.show(duration);
  }

  public setStackHighlight(tileStackId: string, show: boolean, immediate = false): void {
    const highlight = this.getHighlight(tileStackId);
    const duration = immediate ? 0 : undefined;
    if (show) {
      highlight?.show(duration);
    } else {
      highlight?.hide(duration);
    }
  }

  public setXYHighlight(x: number, y: number, show: boolean, immediate = false): void {
    const stack = this.boardView.getModel().getStack(x, y);
    if (!stack) return;
    this.setStackHighlight(stack.id, show, immediate);
  }

  private getModeForStack(stackId: string): TileStackHighlightDisplayMode {
    const model = this.boardView.getModel();
    const stack = model.getStackById(stackId);
    if (!stack) return TileStackHighlightDisplayMode.Normal;
    const tilesAmount = model.getStack(stack.x, stack.y + 1)?.getTileCount() ?? 0;
    return tilesAmount >= BoardHighlightLayer.tilesForTallHighlight
      ? TileStackHighlightDisplayMode.Tall
      : TileStackHighlightDisplayMode.Normal;
  }
}
